<script setup lang="ts">
/**
 * Rangée de grandes cartes qui défile, modèle apple.com/fr/iphone « Tant de
 * raisons d'acheter votre iPhone chez Apple » (Karim 2026-10-18) : grand titre
 * + lien à droite, cartes blanches sur fond gris (petit surtitre, titre fort,
 * une ligne, image en bas, rond noir « › »), flèches ‹ ›. La rangée déborde à
 * droite de l'écran et reste alignée sur la colonne du site à gauche.
 *
 * Mesures relevées sur apple.com (2026-10-18) — carte / surtitre / titre /
 * texte : ≥ 1069 px 372×576 · 17/28/17 ; 735-1068 px 344×501 · 14/24/14 ;
 * téléphone 260×419 · 14/21/14. Grand titre 56 / 48 / 28 px.
 *
 * Réutilisé sur les pages produit (/cagnotte, /qr-codes…). Le paragraphe
 * sous le titre passe par le slot `intro` (texte riche, i18n-t).
 */
import type { CibleLien } from "~/types/cibleLien";
import { insecables } from "~/utils/insecables";
import { TITRE_CARTE_APPLE, TITRE_SECTION_APPLE } from "~/utils/titresApple";

export interface CarteApple {
  surtitre: string;
  titre: string;
  texte: string;
  image: string;
  alt: string;
  href: string;
  /** `photo` = remplit le bas de la carte ; `ecran` (défaut) = capture qui sort du bas. */
  typeImage?: "photo" | "ecran";
}

const props = defineProps<{
  titre: string;
  cartes: CarteApple[];
  lienTexte?: string;
  lienHref?: CibleLien;
  /** Source du clic (directive v-register-cta) quand le lien mène à l'inscription. */
  lienRegisterCta?: string;
}>();

const { t } = useI18n({ useScope: "local" });

// Jamais de ponctuation seule en début de ligne.
const titreAffiche = computed(() => insecables(props.titre));

const rangee = ref<HTMLElement | null>(null);
function defiler(sens: 1 | -1) {
  const carte = rangee.value?.firstElementChild as HTMLElement | null;
  if (!rangee.value || !carte) return;
  rangee.value.scrollBy({ left: sens * (carte.offsetWidth + 20), behavior: "smooth" });
}
</script>

<template>
  <section class="overflow-hidden bg-[#f5f5f7] py-20 lg:py-28">
    <div class="mx-auto mb-10 flex max-w-[1120px] flex-col gap-6 px-4 sm:px-8 lg:mb-12 lg:flex-row lg:items-end lg:justify-between lg:px-0">
      <div class="max-w-[760px]">
        <h2 class="titre-rangee mb-5 text-[#1d1d1f] [text-wrap:balance]" :style="TITRE_SECTION_APPLE">{{ titreAffiche }}</h2>
        <div v-if="$slots.intro" class="text-[17px] font-medium leading-[1.5] text-[#424245]">
          <slot name="intro" />
        </div>
      </div>
      <NuxtLink
        v-if="lienTexte && lienHref"
        v-register-cta="lienRegisterCta"
        :to="lienHref"
        class="inline-flex shrink-0 items-center gap-0.5 text-[17px] font-semibold text-(--color-main) hover:underline"
      >
        {{ lienTexte }}
        <UIcon name="i-lucide-chevron-right" class="size-4" />
      </NuxtLink>
    </div>

    <div ref="rangee" class="rangee flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4">
      <NuxtLink
        v-for="carte in cartes"
        :key="carte.titre"
        :to="carte.href"
        class="carte group relative flex shrink-0 snap-start flex-col overflow-hidden rounded-[28px] bg-white"
      >
        <div class="carte-texte">
          <p class="carte-surtitre mb-2 font-semibold text-[#1d1d1f]">{{ carte.surtitre }}</p>
          <h3 class="carte-titre mb-2.5 text-[#1d1d1f] [text-wrap:balance]" :style="TITRE_CARTE_APPLE">{{ insecables(carte.titre) }}</h3>
          <p class="carte-ligne leading-[1.45] text-[#1d1d1f]">{{ insecables(carte.texte) }}</p>
        </div>
        <div class="relative mt-4 flex-1 overflow-hidden">
          <NuxtImg
            :src="carte.image"
            :alt="carte.alt"
            loading="lazy"
            sizes="260px md:344px lg:372px"
            :class="
              carte.typeImage === 'photo'
                ? 'absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]'
                : 'absolute left-1/2 top-0 w-[66%] -translate-x-1/2 rounded-t-[20px] shadow-[0_20px_50px_-20px_rgba(0,0,0,0.3)] ring-1 ring-black/5 transition-transform duration-500 group-hover:-translate-y-1'
            "
          />
        </div>
        <span class="absolute bottom-5 right-5 flex size-9 items-center justify-center rounded-full bg-[#1d1d1f] text-white transition-transform group-hover:scale-105 lg:size-11" aria-hidden="true">
          <UIcon name="i-lucide-chevron-right" class="size-5" />
        </span>
      </NuxtLink>
    </div>

    <div class="mx-auto mt-6 flex max-w-[1120px] justify-end gap-3 px-4 sm:px-8 lg:px-0">
      <button type="button" class="fleche" :aria-label="t('precedent')" @click="defiler(-1)">
        <UIcon name="i-lucide-chevron-left" class="size-5" />
      </button>
      <button type="button" class="fleche" :aria-label="t('suivant')" @click="defiler(1)">
        <UIcon name="i-lucide-chevron-right" class="size-5" />
      </button>
    </div>
  </section>
</template>

<style scoped>
/* ── Mesures apple.com : téléphone / tablette (≥ 735 px) / ordinateur (≥ 1069 px) ── */
.titre-rangee { font-size: 28px; }
.carte { width: 260px; height: 419px; }
.carte-texte { padding: 24px 24px 0; }
.carte-surtitre, .carte-ligne { font-size: 14px; }
.carte-titre { font-size: 21px; }
@media (min-width: 735px) {
  .titre-rangee { font-size: 48px; }
  .carte { width: 344px; height: 501px; }
  .carte-texte { padding: 30px 30px 0; }
  .carte-titre { font-size: 24px; }
}
@media (min-width: 1069px) {
  .titre-rangee { font-size: 56px; }
  .carte { width: 372px; height: 576px; }
  .carte-texte { padding: 32px 32px 0; }
  .carte-surtitre, .carte-ligne { font-size: 17px; }
  .carte-titre { font-size: 28px; }
}

/* Rangée alignée sur la colonne de 1120 px, qui déborde à droite ; barre de
   défilement masquée (flèches et doigt). */
.rangee {
  padding-inline: max(1rem, calc((100vw - 1120px) / 2));
  scroll-padding-inline: max(1rem, calc((100vw - 1120px) / 2));
  scrollbar-width: none;
}
.rangee::-webkit-scrollbar { display: none; }
@media (min-width: 640px) {
  .rangee {
    padding-inline: max(2rem, calc((100vw - 1120px) / 2));
    scroll-padding-inline: max(2rem, calc((100vw - 1120px) / 2));
  }
}

.fleche {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 9999px;
  background: #e8e8ed;
  color: #1d1d1f;
  transition: background-color 0.2s ease;
}
.fleche:hover { background: #d2d2d7; }
</style>

<i18n lang="json" src="../../../i18n/pages/components/shared/CarrouselCartesApple.json"></i18n>
